import { useEffect, useState } from 'react';
import {
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Typography,
  TextField,
} from '@mui/material';
import axios from 'axios';

import config from '../config/config.json';
import { PickTeamPlayer } from '../types';
import { LoadingButton } from '@mui/lab';

type PlayerListProps = {
  onDraw: (result: {
    players: PickTeamPlayer[];
    numberOfWeeks: number;
    numberOfTeams: number;
    captains: string[];
    startDate: string;
  }) => void;
};

export default function PickingTeamForm({ onDraw }: Readonly<PlayerListProps>) {
  const [fetching, setFetching] = useState(false);
  const [numWeeks, setNumWeeks] = useState<number>(3);
  const [numberOfTeams, setNumberOfTeams] = useState<number>(4);
  const [captains, setCaptains] = useState<string[]>([]);
  const [errors, setErrors] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setErrors(Array(numberOfTeams).fill(''));
  }, [numberOfTeams]);

  const fetchPlayers = () => {
    setFetching(true);
    setError(null);
    axios
      .get(config.webAppURL, {
        params: {
          pickingMode: true,
          numWeeks,
        },
      })
      .then((resp) => {
        if (resp.data.error) {
          setError(resp.data.error);
          return;
        }
        const players: any = Object.keys(resp.data.players).map((name) => ({
          name,
          appearances: resp.data.players[name].appearances,
          goals: resp.data.players[name].goals,
          assists: resp.data.players[name].assists,
        }));
        // Check if captains are players, if not set errors
        captains.forEach((captain, index) => {
          if (!players.some((player: any) => player.name === captain)) {
            errors[index] = 'Player not found';
          } else {
            errors[index] = '';
          }
        });
        setErrors([...errors]);

        if (errors.some((error) => error !== '')) {
          return;
        }
        const numberOfWeeks = resp.data.numberOfWeeks;
        const startDate = resp.data.startDate;
        onDraw({ players, numberOfWeeks, numberOfTeams, captains, startDate });
      })
      .catch((e) => {
        console.error(e);
        setError('Error fetching players');
      })
      .finally(() => {
        setFetching(false);
      });
  };

  const isValidForm =
    captains.length === numberOfTeams &&
    captains.every((captain) => captain.trim() !== '') &&
    new Set(captains).size === numberOfTeams;

  return (
    <Stack direction="column">
      <Typography variant="body2" sx={{ mb: 2 }}>
        Pick teams for Huhi Superleague seasons
      </Typography>
      {error && (
        <Typography variant="body2" color="error">
          {error}
        </Typography>
      )}
      <form>
        <Stack direction="row" spacing={2} sx={{ mb: 2, mt: 3 }}>
          <FormControl sx={{ flexGrow: 1 }}>
            <InputLabel>Number of weeks</InputLabel>
            <Select
              id="numWeeks"
              name="numWeeks"
              label="Number of weeks"
              value={numWeeks}
              onChange={(event) =>
                setNumWeeks(parseInt(event.target.value as string))
              }
              disabled={fetching}
            >
              <MenuItem value={2}>2</MenuItem>
              <MenuItem value={3}>3</MenuItem>
              <MenuItem value={4}>4</MenuItem>
            </Select>
          </FormControl>
          <FormControl sx={{ flexGrow: 1 }}>
            <InputLabel>Number of teams</InputLabel>
            <Select
              id="numTeams"
              name="numTeams"
              label="Number of teams"
              value={numberOfTeams}
              onChange={(event) =>
                setNumberOfTeams(parseInt(event.target.value as string))
              }
              disabled={fetching}
            >
              <MenuItem value={3}>3</MenuItem>
              <MenuItem value={4}>4</MenuItem>
            </Select>
          </FormControl>
        </Stack>
        <Stack direction="column">
          {Array(numberOfTeams)
            .fill(0)
            .map((_, index) => (
              <FormControl key={index} sx={{ flexGrow: 1, mb: 2 }}>
                <TextField
                  id={`captain${index}`}
                  required
                  name={`captain${index}`}
                  label={`Captain for team ${index + 1}`}
                  value={captains[index] || ''}
                  onChange={(event) => {
                    const newCaptains = [...captains];
                    newCaptains[index] = event.target.value;
                    setCaptains(newCaptains);
                  }}
                  disabled={fetching}
                  error={!!errors[index]}
                  helperText={errors[index]}
                />
              </FormControl>
            ))}
        </Stack>
        <LoadingButton
          variant="contained"
          type="submit"
          fullWidth
          loading={fetching}
          disabled={fetching || !isValidForm}
          onClick={fetchPlayers}
        >
          Draw
        </LoadingButton>
      </form>
    </Stack>
  );
}
